import axios from 'axios'
import { attachAxiosAuth } from '../utils/axiosAuthInterceptors'
import { apiBaseURL } from '@/config/apiBase'

// Shared axios instance for all `api.get` / `usersAPI` calls (see `export default api`)
const api = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json'
  }
})

attachAxiosAuth(api)

/**
 * The API client defaults to Content-Type: application/json. For FormData, axios would
 * otherwise run the JSON transform and corrupt the body; clearing Content-Type first lets
 * the browser set multipart boundaries so every `attachments` part reaches the server.
 */
function multipartFormDataRequestConfig() {
  const defaultsChain = axios.defaults.transformRequest
  const tail = Array.isArray(defaultsChain)
    ? [...defaultsChain]
    : defaultsChain != null
      ? [defaultsChain]
      : []
  return {
    transformRequest: [
      (data, headers) => {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          if (headers && typeof headers.delete === 'function') {
            headers.delete('Content-Type')
          } else if (headers) {
            delete headers['Content-Type']
          }
        }
        return data
      },
      ...tail,
    ],
  }
}

// Email API
export const emailAPI = {
  /**
   * Get list of emails with pagination, search, and sorting
   * @param {Object} params - Query parameters
   * @returns {Promise} Email list response
   */
  getEmails(params = {}) {
    return api.get('/api/emails', { params })
  },

  /**
   * Get email by ID with full details
   * @param {number} id - Email ID
   * @returns {Promise} Email detail response
   */
  getEmailById(id) {
    return api.get(`/api/emails/${id}`)
  },

  /**
   * Manually trigger email sync from IMAP
   * @param {string} folder - IMAP folder to sync (default: 'INBOX')
   * @returns {Promise} Sync result response
   */
  syncEmails(folder = 'INBOX') {
    return api.post('/api/emails/sync', { folder })
  },

  /**
   * Reply to an email
   * @param {number} id - Email ID to reply to
   * @param {string} body - HTML body of the reply
   * @param {File[]|null} files - Optional attachments (multipart)
   * @param {boolean} [replyAll] - If true, include original To/CC/BCC (except our mailbox and primary recipient)
   * @returns {Promise} Reply result response
   */
  replyEmail(id, body, files = null, replyAll = false) {
    if (files && files.length > 0) {
      const fd = new FormData()
      fd.append('body', body)
      fd.append('replyAll', replyAll ? 'true' : 'false')
      for (const f of files) {
        fd.append('attachments', f)
      }
      return api.post(`/api/emails/${id}/reply`, fd, multipartFormDataRequestConfig())
    }
    return api.post(`/api/emails/${id}/reply`, { body, replyAll })
  },

  /**
   * Compose new email to customer; creates ticket (Complete / Resolve).
   * @param {FormData} formData - to, subject, body, mailboxId, useManualFrom, manualFromEmail?, cc?, bcc?, attachments
   */
  composeEmail(formData) {
    return api.post('/api/emails/compose', formData, multipartFormDataRequestConfig())
  },

  /**
   * Manual recovery: create ticket and link email (inbound, no ticket yet).
   * @param {number} id - Email ID
   */
  createTicketFromEmail(id) {
    return api.post(`/api/emails/${id}/create-ticket`)
  },

  /**
   * Update email read status
   * @param {number|Array<number>} id - Email ID or array of IDs
   * @param {boolean} is_read - Read status
   * @param {Array<number>} emailIds - For bulk updates
   * @returns {Promise} Update response
   */
  updateEmailRead(id, is_read, emailIds = null) {
    if (emailIds && Array.isArray(emailIds)) {
      return api.patch('/api/emails/read', { is_read, emailIds })
    }
    return api.patch(`/api/emails/${id}/read`, { is_read })
  },

  /**
   * Update email tags
   * @param {number|Array<number>} id - Email ID or array of IDs
   * @param {Array|Object|string|null} tags - Tags to set
   * @param {Array<number>} emailIds - For bulk updates
   * @returns {Promise} Update response
   */
  updateEmailTags(id, tags, emailIds = null) {
    if (emailIds && Array.isArray(emailIds)) {
      return api.patch('/api/emails/tags', { tags, emailIds })
    }
    return api.patch(`/api/emails/${id}/tags`, { tags })
  },

  /**
   * Update email assignment
   * @param {number|Array<number>} id - Email ID or array of IDs
   * @param {string|null} assigned_to - Assigned user/agent
   * @param {Array<number>} emailIds - For bulk updates
   * @returns {Promise} Update response
   */
  updateEmailAssign(id, assigned_to, emailIds = null) {
    if (emailIds && Array.isArray(emailIds)) {
      return api.patch('/api/emails/assign', { assigned_to, emailIds })
    }
    return api.patch(`/api/emails/${id}/assign`, { assigned_to })
  }
}

// Attachment API
export const attachmentAPI = {
  /**
   * Download attachment file
   * @param {number} id - Attachment ID
   * @returns {Promise} File blob
   */
  downloadAttachment(id) {
    return api.get(`/api/attachments/${id}/download`, {
      responseType: 'blob'
    })
  }
}

// Ticket API
export const ticketAPI = {
  /**
   * Get list of tickets with pagination, filtering, and search
   * @param {Object} params - Query parameters
   * @returns {Promise} Ticket list response
   */
  getTickets(params = {}) {
    return api.get('/api/tickets', { params })
  },

  /**
   * Merge-source tickets (rows with merged_into_ticket_id), paginated with filters.
   * @param {Object} params - page, limit, search, status, subStatus, mergedIntoTicketId, mergedStartDate, mergedEndDate, mergedByUserId (comma-separated user ids), sortBy, sortOrder, includeJunk
   */
  getMergedTickets(params = {}) {
    return api.get('/api/tickets/merged', { params })
  },

  /**
   * Get ticket by ID with full details and emails
   * @param {number} id - Ticket ID
   * @returns {Promise} Ticket detail response
   */
  getTicketById(id) {
    return api.get(`/api/tickets/${id}`)
  },

  /**
   * Download a ticket-level attachment (manual ticket files).
   * @param {number|string} ticketId
   * @param {number|string} attachmentId
   */
  downloadTicketAttachment(ticketId, attachmentId) {
    return api.get(`/api/tickets/${ticketId}/attachments/${attachmentId}`, {
      responseType: 'blob'
    })
  },

  /**
   * Get emails (thread) for a ticket. Same shape as ticket.emails in ticket detail.
   * @param {number} ticketId - Ticket ID
   * @returns {Promise} { success, data: emails[] }
   */
  getTicketEmails(ticketId) {
    return api.get(`/api/tickets/${ticketId}/emails`)
  },

  /**
   * Update ticket status (with optional sub-status, nature, feedback, callback required)
   * @param {number} id - Ticket ID
   * @param {Object} payload - { status, subStatus?, categoryId?, feedback?, callbackRequired? }
   * @returns {Promise} Update response
   */
  updateTicketStatus(id, payload) {
    return api.patch(`/api/tickets/${id}/status`, payload)
  },

  /**
   * Get ticket categories (hierarchy) for picker
   * @returns {Promise} { success, data: Array }
   */
  getTicketCategories() {
    return api.get('/api/tickets/categories')
  },

  /**
   * Create a new ticket (manual / outbound)
   * @param {Object} payload - { title or subject, priority?, status?, subStatus?, remarks?, assignToUserId?, categoryId? }
   * @param {File[]|null} files - Optional array of File objects for attachments
   * @returns {Promise} { success, data: created ticket }
   */
  createTicket(payload, files = null) {
    if (files && files.length > 0) {
      const form = new FormData()
      const subject = payload.title != null ? payload.title : payload.subject
      if (subject != null) form.append('title', subject)
      if (payload.priority != null) form.append('priority', payload.priority)
      if (payload.status != null) form.append('status', payload.status)
      if (payload.subStatus != null) form.append('subStatus', payload.subStatus)
      if (payload.remarks != null) form.append('remarks', payload.remarks)
      if (payload.assignToUserId != null) form.append('assignToUserId', payload.assignToUserId)
      if (payload.categoryId != null) form.append('categoryId', payload.categoryId)
      for (const file of files) form.append('attachments', file)
      return api.post('/api/tickets', form, multipartFormDataRequestConfig())
    }
    const body = {
      title: payload.title != null ? payload.title : payload.subject,
      priority: payload.priority ?? 'Medium',
      status: payload.status ?? 'Pending',
      subStatus: payload.subStatus ?? undefined,
      remarks: payload.remarks || undefined,
      assignToUserId: payload.assignToUserId ?? undefined,
      categoryId: payload.categoryId ?? undefined
    }
    return api.post('/api/tickets', body)
  },

  /**
   * Poll the status of an async ticket-creation job (manual ticket / compose-new).
   * @param {string} jobId
   * @returns {Promise} { success, status, ticketId, emailId, failedReason }
   */
  getTicketCreationJobStatus(jobId) {
    return api.get(`/api/tickets/creation-jobs/${encodeURIComponent(jobId)}`)
  },

  /**
   * Poll a ticket-creation job until it completes/fails or times out.
   * Resolves with the created ticketId on success.
   * @param {string} jobId
   * @param {{ intervalMs?: number, timeoutMs?: number }} [opts]
   * @returns {Promise<{ ticketId: number|null, emailId: number|null }>}
   */
  async waitForTicketCreationJob(jobId, opts = {}) {
    const intervalMs = opts.intervalMs ?? 1000
    const timeoutMs = opts.timeoutMs ?? 60000
    const start = Date.now()
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let res
      try {
        res = await this.getTicketCreationJobStatus(jobId)
      } catch (err) {
        // 404 = unknown (completed+evicted or gone). Treat as done without a ticketId.
        if (err && err.response && err.response.status === 404) {
          return { ticketId: null, emailId: null }
        }
        throw err
      }
      const data = res.data || res
      const status = data.status
      if (status === 'completed') {
        return { ticketId: data.ticketId ?? null, emailId: data.emailId ?? null }
      }
      if (status === 'failed') {
        const reason = data.failedReason || 'Ticket creation failed'
        throw new Error(reason)
      }
      if (Date.now() - start > timeoutMs) {
        throw new Error('Timed out waiting for ticket creation')
      }
      await new Promise((r) => setTimeout(r, intervalMs))
    }
  },

  /**
   * Get status change history for a ticket
   * @param {number} id - Ticket ID
   * @returns {Promise} { success, data: Array }
   */
  getTicketStatusHistory(id) {
    return api.get(`/api/tickets/${id}/status-history`)
  },

  /**
   * Update ticket priority
   * @param {number} id - Ticket ID
   * @param {string} priority - New priority
   * @returns {Promise} Update response
   */
  updateTicketPriority(id, priority) {
    return api.patch(`/api/tickets/${id}/priority`, { priority })
  },

  /**
   * Manually assign ticket to a user (or unassign). Remarks removed.
   * @param {number} id - Ticket ID
   * @param {number|null} userId - User ID to assign, or null to unassign
   * @returns {Promise} Updated ticket
   */
  assignTicket(id, userId) {
    return api.patch(`/api/tickets/${id}/assign`, { userId })
  },

  /**
   * Get assignment history for a ticket (all previous assigns with agent name, agent type, assign date)
   * @param {number} id - Ticket ID
   * @returns {Promise} { success, data: Array }
   */
  getTicketAssignments(id) {
    return api.get(`/api/tickets/${id}/assignments`)
  },

  /**
   * Get internal notes for a ticket
   * @param {number} id - Ticket ID
   * @returns {Promise} { success, data: Array }
   */
  getTicketNotes(id) {
    return api.get(`/api/tickets/${id}/notes`)
  },

  /**
   * Add an internal note to a ticket (optional file attachments).
   * @param {number} id - Ticket ID
   * @param {string} noteText - Note content (may be empty if files provided)
   * @param {File[]} [files] - Optional attachments
   * @returns {Promise} { success, data: note }
   */
  addTicketNote(id, noteText, files = []) {
    const list = Array.isArray(files) ? files : []
    if (list.length > 0) {
      const form = new FormData()
      form.append('noteText', noteText != null ? String(noteText) : '')
      for (const f of list) {
        form.append('attachments', f)
      }
      return api.post(`/api/tickets/${id}/notes`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }
    return api.post(`/api/tickets/${id}/notes`, { noteText })
  },

  /**
   * Download an internal note attachment.
   */
  downloadInternalNoteAttachment(ticketId, noteId, attachmentId) {
    return api.get(
      `/api/tickets/${ticketId}/notes/${noteId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
    )
  },

  /**
   * Get unified activity timeline for a ticket (status, assignment, priority, notes)
   * @param {number} id - Ticket ID
   * @returns {Promise} { success, data: Array }
   */
  getTicketActivity(id) {
    return api.get(`/api/tickets/${id}/activity`)
  },

  /**
   * Bulk assign tickets to agents
   * @param {Object} body - { ticketIds: number[]|null, agentIds: number[], assignmentType: 'single'|'round_robin'|'load_balance' }
   * @returns {Promise<{ data: { success: boolean, assignedCount: number } }>}
   */
  bulkAssign(body) {
    return api.post('/api/tickets/bulk-assign', body)
  },

  /**
   * Bulk unassign tickets (remove current assignee)
   * @param {number[]} ticketIds
   */
  bulkUnassign(ticketIds) {
    return api.post('/api/tickets/bulk-unassign', { ticketIds })
  },
  bulkJunk(ticketIds, isJunk = true) {
    return api.post('/api/tickets/bulk-junk', { ticketIds, isJunk })
  },

  /**
   * Mark ticket as junk or restore from junk
   * @param {number} id - Ticket ID
   * @param {boolean} isJunk - true = move to junk, false = restore
   */
  setTicketJunk(id, isJunk) {
    return api.patch(`/api/tickets/${id}/junk`, { isJunk })
  },

  /**
   * Preview a merge operation and return warnings/diagnostics.
   * @param {number[]} ticketIds
   */
  previewMerge(ticketIds) {
    return api.post('/api/tickets/merge/preview', { ticketIds })
  },

  /**
   * Merge tickets into canonical ticket.
   * @param {{ ticketIds: number[], force?: boolean }} body
   */
  mergeTickets(body) {
    return api.post('/api/tickets/merge', body)
  },

  /**
   * Tickets sharing the same inbound From/To-derived customer identity (keyset pagination).
   * @param {number} id - Ticket ID
   * @param {{ limit?: number, cursorCreatedAt?: string, cursorId?: number }} params
   */
  getRelatedTickets(id, params = {}) {
    return api.get(`/api/tickets/${id}/related`, { params })
  }
}

/** Canned reply templates (list/search paginated; write: admin/manager/team_leader) */
export const replyTemplateAPI = {
  /** Full list (no pagination) for ticket reply picker — same auth as list. */
  listAll() {
    return api.get('/api/reply-templates', { params: { all: '1' } })
  },
  list(params = {}) {
    return api.get('/api/reply-templates', { params })
  },
  getById(id) {
    return api.get(`/api/reply-templates/${id}`)
  },
  create(body) {
    return api.post('/api/reply-templates', body)
  },
  update(id, body) {
    return api.put(`/api/reply-templates/${id}`, body)
  },
  remove(id) {
    return api.delete(`/api/reply-templates/${id}`)
  }
}

export const ticketEmailFilterAPI = {
  list() {
    return api.get('/api/ticket-email-filters')
  },
  getById(id) {
    return api.get(`/api/ticket-email-filters/${id}`)
  },
  create(body) {
    return api.post('/api/ticket-email-filters', body)
  },
  createMany(emails) {
    return api.post('/api/ticket-email-filters', { emails })
  },
  update(id, body) {
    return api.put(`/api/ticket-email-filters/${id}`, body)
  },
  remove(id) {
    return api.delete(`/api/ticket-email-filters/${id}`)
  }
}

/** App configuration settings (unique key id per row; no delete) */
export const configurationSettingsAPI = {
  list() {
    return api.get('/api/configuration-settings')
  },
  create(body) {
    return api.post('/api/configuration-settings', body)
  },
  update(id, body) {
    return api.put(`/api/configuration-settings/${id}`, body)
  },
}

/** Master configuration key names (manual DB inserts; read-only) */
export const configurationKeyNameAPI = {
  getAll() {
    return api.get('/api/configuration-key-names')
  },
}

/** Follow-up templates (hours delay, body, is_active; write: admin/manager/team_leader) */
export const followUpTemplateAPI = {
  listAll() {
    return api.get('/api/follow-up-templates', { params: { all: '1' } })
  },
  list(params = {}) {
    return api.get('/api/follow-up-templates', { params })
  },
  getById(id) {
    return api.get(`/api/follow-up-templates/${id}`)
  },
  create(body) {
    return api.post('/api/follow-up-templates', body)
  },
  update(id, body) {
    return api.put(`/api/follow-up-templates/${id}`, body)
  },
  remove(id) {
    return api.delete(`/api/follow-up-templates/${id}`)
  }
}

/** Acknowledgement templates (separate CRUD from reply templates) */
export const acknowledgementTemplateAPI = {
  listAll() {
    return api.get('/api/acknowledgement-templates', { params: { all: '1' } })
  },
  list(params = {}) {
    return api.get('/api/acknowledgement-templates', { params })
  },
  getById(id) {
    return api.get(`/api/acknowledgement-templates/${id}`)
  },
  create(body) {
    return api.post('/api/acknowledgement-templates', body)
  },
  update(id, body) {
    return api.put(`/api/acknowledgement-templates/${id}`, body)
  },
  remove(id) {
    return api.delete(`/api/acknowledgement-templates/${id}`)
  }
}

/** Singleton: auto-reply when a new ticket is created from inbound email sync */
export const ticketAcknowledgementSettingsAPI = {
  get() {
    return api.get('/api/settings/ticket-acknowledgement')
  },
  put(body) {
    return api.put('/api/settings/ticket-acknowledgement', body)
  }
}

// Ticket Categories API (admin/manager)
export const ticketCategoriesAPI = {
  /**
   * List ticket categories
   * @param {'tree'|'flat'} mode
   * @returns {Promise} { success, data }
   */
  list(mode = 'tree') {
    return api.get('/api/ticket-categories', {
      params: { mode }
    })
  },

  /**
   * Create a new ticket category
   * @param {{ name: string, parentId?: number|null, sortOrder?: number }} data
   */
  create(data) {
    return api.post('/api/ticket-categories', data)
  },

  /**
   * Update ticket category
   * @param {number} id
   * @param {{ name?: string, parentId?: number|null, sortOrder?: number }} data
   */
  update(id, data) {
    return api.put(`/api/ticket-categories/${id}`, data)
  },

  /**
   * Delete ticket category
   * @param {number} id
   */
  remove(id) {
    return api.delete(`/api/ticket-categories/${id}`)
  },

  // Level-specific helpers
  listL2() {
    return api.get('/api/ticket-categories/l2')
  },
  createL2(data) {
    return api.post('/api/ticket-categories/l2', data)
  },
  updateL2(id, data) {
    return api.put(`/api/ticket-categories/l2/${id}`, data)
  },
  deleteL2(id) {
    return api.delete(`/api/ticket-categories/l2/${id}`)
  },
  listL3(parentId) {
    return api.get('/api/ticket-categories/l3', { params: { parentId } })
  },
  createL3(data) {
    return api.post('/api/ticket-categories/l3', data)
  },
  updateL3(id, data) {
    return api.put(`/api/ticket-categories/l3/${id}`, data)
  },
  deleteL3(id) {
    return api.delete(`/api/ticket-categories/l3/${id}`)
  },
  listL4(parentId) {
    return api.get('/api/ticket-categories/l4', { params: { parentId } })
  },
  createL4(data) {
    return api.post('/api/ticket-categories/l4', data)
  },
  updateL4(id, data) {
    return api.put(`/api/ticket-categories/l4/${id}`, data)
  },
  deleteL4(id) {
    return api.delete(`/api/ticket-categories/l4/${id}`)
  }
}

// Users API (for assignee dropdown - admin/manager can list users)
export const usersAPI = {
  getUsers(params = {}) {
    return api.get('/api/users', { params })
  },
  getUser(id, params = {}) {
    return api.get(`/api/users/${id}`, { params })
  },
  updateUser(id, payload) {
    return api.patch(`/api/users/${id}`, payload)
  },
  disableUser(id) {
    return api.patch(`/api/users/${id}/disable`)
  },
  enableUser(id) {
    return api.patch(`/api/users/${id}/enable`)
  },
  softDeleteUser(id) {
    return api.delete(`/api/users/${id}`)
  },
  restoreUser(id) {
    return api.post(`/api/users/${id}/restore`)
  }
}

export const generalusersAPI = {
  getUsers() {
    return api.get('/api/users/generalusers')
  },
  getAvailableGeneralAgents() {
    return api.get('/api/agents/available-general-users')
  }
}


// Mailbox API
export const mailboxAPI = {
  /**
   * Get all mailboxes with sync state
   * @returns {Promise} Mailbox list response
   */
  getMailboxes() {
    return api.get('/api/mailboxes')
  },

  /**
   * Get mailbox by ID
   * @param {number} id - Mailbox ID
   * @returns {Promise} Mailbox detail response
   */
  getMailboxById(id) {
    return api.get(`/api/mailboxes/${id}`)
  },

  /**
   * Create a new mailbox
   * @param {Object} mailboxData - Mailbox data (optional initial_sync_from_date: YYYY-MM-DD, first sync only)
   * @returns {Promise} Create response
   */
  createMailbox(mailboxData) {
    return api.post('/api/mailboxes', mailboxData)
  },

  /**
   * Update a mailbox
   * @param {number} id - Mailbox ID
   * @param {Object} updates - Fields to update (optional initial_sync_from_date: YYYY-MM-DD or null)
   * @returns {Promise} Update response
   */
  updateMailbox(id, updates) {
    return api.put(`/api/mailboxes/${id}`, updates)
  },

  /**
   * Delete a mailbox
   * @param {number} id - Mailbox ID
   * @returns {Promise} Delete response
   */
  deleteMailbox(id) {
    return api.delete(`/api/mailboxes/${id}`)
  },

  /**
   * Test IMAP connection for a mailbox
   * @param {number} id - Mailbox ID
   * @returns {Promise} Test connection response
   */
  testConnection(id) {
    return api.post(`/api/mailboxes/${id}/test-connection`)
  },

  /**
   * Test IMAP connection with provided credentials (for new mailboxes)
   * @param {Object} credentials - IMAP credentials
   * @returns {Promise} Test connection response
   */
  testConnectionWithCredentials(credentials) {
    return api.post('/api/mailboxes/test-connection', credentials)
  }
}

// Available agents for assignment (active, available, not suspended)
export const agentsAPI = {
  /**
   * GET /api/agents/available
   * @returns {Promise<{ data: Array<{ id: number, name: string, currentOpenTicketCount: number }> }>}
   */
  getAvailable() {
    return api.get('/api/agents/available')
  },
  getSheetRoles() {
    return api.get('/api/agents/sheet-roles')
  },
  getAvailableBySheetRole(sheetRole) {
    return api.get('/api/agents/available-by-sheet-role', {
      params: { sheetRole }
    })
  }
}

/** Agent my-dashboard charts (split load; full dashboard uses GET /summary only) */
export const AGENT_DASHBOARD_CHART_KEYS = [
  'sub-status',
  'per-agent-productivity',
  'dispose-by-agent',
]

/** Dashboard analytics (admin / manager / team_leader) */
export const dashboardAPI = {
  /** Overall + calendar today/yesterday KPIs (not affected by period). */
  getDashboardSnapshot(params = {}) {
    return api.get('/api/dashboard/snapshot', { params })
  },
  /** Period-scoped KPIs (sub-status, assignment, dispose in selected range). */
  getDashboardPeriod(params = {}) {
    return api.get('/api/dashboard/period', { params })
  },
  /**
   * Merged snapshot + period (single round-trip).
   * @param {Record<string, string|number|undefined>} params - period, displayTimeZone, fromDate, toDate
   */
  getSummary(params = {}) {
    return api.get('/api/dashboard/summary', { params })
  },
  /** Live View — today KPI cards vs yesterday (displayTimeZone + optional filters). */
  getLiveView(params = {}) {
    return api.get('/api/dashboard/live', { params })
  },
  /**
   * Scoped to logged-in agent only (same shape as getSummary).
   */
  getAgentSummary(params = {}) {
    return api.get('/api/dashboard/agent-summary', { params })
  },
  getAgentStats(params = {}) {
    return api.get('/api/dashboard/agent-stats', { params })
  },
  getAgentChart(chartKey, params = {}) {
    return api.get(`/api/dashboard/agent/charts/${encodeURIComponent(chartKey)}`, { params })
  },
  /**
   * Custom dashboard — agent performance table.
   * @param {{ period?: 'today'|'yesterday'|'this_week'|'last_week'|'this_month'|'last_month'|'custom', displayTimeZone?: string, startAt?: string, endAt?: string }} params
   */
  getCustomAgentPerformance(params = {}) {
    return api.get('/api/dashboard/custom/agent-performance', { params })
  },
  /**
   * Drill-down rows for custom dashboard disposition columns.
   * @param {{ metric: 'pending'|'resolved'|'total', userId?: number|string|null, period?: string, displayTimeZone?: string, startAt?: string, endAt?: string }} params
   */
  getCustomAgentPerformanceDetails(params = {}) {
    return api.get('/api/dashboard/custom/agent-performance/details', { params })
  },
  /** Sync .xlsx download for drill-down (metric + agent + period; all matching rows). */
  exportCustomAgentPerformanceDetails(params = {}) {
    return api.get('/api/dashboard/custom/agent-performance/details/export', {
      params,
      responseType: 'blob',
    })
  },
  /** Sync .xlsx download (same params as getCustomAgentPerformance). */
  exportCustomAgentPerformance(params = {}) {
    return api.get('/api/dashboard/custom/agent-performance/export', {
      params,
      responseType: 'blob',
    })
  },
  /**
   * CSAT dashboard — KPI cards vs prior period, rating breakdown, survey list.
   * @param {{ period?: string, displayTimeZone?: string, startAt?: string, endAt?: string, limit?: number }} params
   */
  getCsat(params = {}) {
    return api.get('/api/dashboard/csat', { params })
  },
  getCsatComparisonChart(params = {}) {
    return api.get('/api/dashboard/csat/charts/comparison', { params })
  },
  getCsatFeedbackAnalysisChart(params = {}) {
    return api.get('/api/dashboard/csat/charts/feedback-analysis', { params })
  },
  getCsatAgentReport(params = {}) {
    return api.get('/api/dashboard/csat/agent-report', { params })
  },
  /**
   * CSAT Emails List — submitted CSAT responses (received_at IS NOT NULL).
   * @param {{ fromDate: string, toDate: string, displayTimeZone?: string, page?: number, limit?: number, resolveAgentUserIds?: string|number[], ratingScores?: string|number[] }} params
   */
  getCsatEmailsList(params = {}) {
    return api.get('/api/dashboard/csat/emails', { params })
  },
  /**
   * Email Disposition Level 4 — counts by disposition date.
   * @param {{ fromDate: string, toDate: string, displayTimeZone?: string }} params
   */
  getEmailDispositionLevel4(params = {}) {
    return api.get('/api/dashboard/email-disposition/level4', { params })
  },
  /** Automated daily dashboard snapshot backups — list available dates. */
  listSnapshotBackups(params = {}) {
    return api.get('/api/dashboard/snapshot-backups', { params })
  },
  /** Full KPI rows for a date range (Excel export). */
  getSnapshotBackupsRange(params = {}) {
    return api.get('/api/dashboard/snapshot-backups/range', { params })
  },
  getSnapshotBackupByDate(snapshotDate, params = {}) {
    return api.get(`/api/dashboard/snapshot-backups/${encodeURIComponent(snapshotDate)}`, { params })
  },
  getSnapshotBackupById(id) {
    return api.get(`/api/dashboard/snapshot-backups/by-id/${encodeURIComponent(id)}`)
  },
}

/** Customized ticket reports (async Excel jobs). */
export const ticketReportsAPI = {
  /**
   * @param {string} reportType - assign_to_resolve | ticket_report
   * @param {object} params - filters (includeMerged for ticket_report)
   */
  createReport(reportType, params) {
    return api.post('/api/ticket-reports', {
      reportType: reportType || 'assign_to_resolve',
      params
    })
  },
  listReports(params = {}) {
    return api.get('/api/ticket-reports', { params })
  },
  getReport(id) {
    return api.get(`/api/ticket-reports/${id}`)
  },
  downloadReport(id) {
    return api.get(`/api/ticket-reports/${id}/download`, { responseType: 'blob' })
  }
}

export const agentAPI = {
  /**
   * Get current status for a user + agent type. No record => OFFLINE.
   * @param {number} userId
   * @param {number} userAgentTypeId
   * @returns {Promise<{ data: { trigger_action: string, trigger_time: string|null } }>}
   */
  getCurrentStatus(userId, userAgentTypeId) {
    return api.get('/api/agent/current-status', {
      params: { user_id: userId, user_agent_type_id: userAgentTypeId }
    })
  },

  /**
   * Set status (AVAILABLE or OFFLINE). Do not send time; backend uses UTC.
   * @param {Object} body - { user_id, user_agent_type_id, trigger_action: 'AVAILABLE'|'OFFLINE' }
   */
  toggleStatus(body) {
    return api.post('/api/agent/toggle-status', body)
  }
}

// AI ticket analysis (Groq)
export const analyzeAPI = {
  /**
   * Analyse ticket email thread — returns summary and customer intent.
   * @param {Object} ticketPayload - Full ticket object, typically `{ data: ticket }`
   */
  analyzeTicket(ticketPayload) {
    return api.post('/api/analyze/analyze-ticket', ticketPayload)
  },
}

export default api

